import { assertNever } from 'types/entities/shared/result'

import {
  buildAssignSlotPipeline,
  buildChangeDateSlotPipeline,
  buildChangeDayOfWeekSlotPipeline,
  buildMoveSlotPipeline,
  buildResequenceSlotPipeline,
  buildUnassignSlotPipeline,
} from '../pipelines/v3'
import { collectRowsMissingRouteSlotId } from '../slotPayloads'

import { v1BulkStrategy } from './v1Bulk'

import type { SlotRow } from '../slotPayloads'
import type { RouteOpStrategy } from './index'
import type { Deps, OperationPlan, RouteOpIntent, RouteOpsStrategy } from 'types/modules/routing'

const STRATEGY_ID: RouteOpsStrategy = 'v3-slot'

/** Ordering context counts: a resequence submits the full order, so a slot-less row there reads as off the route. */
const addressedRows = (intent: RouteOpIntent): ReadonlyArray<SlotRow> => {
  switch (intent.kind) {
    case 'assign':
    case 'change-date':
      return [...intent.stops, ...(intent.allOrderedStops ?? [])]
    case 'unassign':
      return intent.stops
    case 'move':
      return [...intent.stops, ...(intent.destinationOrderedStops ?? [])]
    case 'resequence':
      return [...intent.orderedStops, ...(intent.masterOrderedStops ?? [])]
    case 'change-day-of-week':
      return [...intent.movedStops, ...(intent.destinationOrderedStops ?? [])]
    default:
      return []
  }
}

/** Whole-plan, never per-row: a short v3 payload would drop stops inside a 200 nobody reads. */
const isFullyAddressable = (intent: RouteOpIntent): boolean => collectRowsMissingRouteSlotId(addressedRows(intent)).length === 0

export const v3SlotStrategy: RouteOpStrategy = {
  id: STRATEGY_ID,
  plan(intent: RouteOpIntent, deps: Deps): OperationPlan {
    if (!isFullyAddressable(intent)) return v1BulkStrategy.plan(intent, deps)

    switch (intent.kind) {
      case 'assign':
        return buildAssignSlotPipeline(intent, deps)
      case 'unassign':
        return buildUnassignSlotPipeline(intent, deps)
      case 'move':
        return buildMoveSlotPipeline(intent, deps)
      case 'resequence':
        return buildResequenceSlotPipeline(intent, deps)
      // v1 `moveStopDate` is a recurrence composite, not a re-date: weekly-recurring gate,
      // duplicate guard, a signed within-week day shift, and hold/action-date re-evaluation.
      // `changeDayOfWeekBySlot` reproduces none of it, so master-side change-date stays on v1.
      case 'change-date':
        return intent.includeMaster === true ? v1BulkStrategy.plan(intent, deps) : buildChangeDateSlotPipeline(intent, deps)
      case 'change-day-of-week':
        return buildChangeDayOfWeekSlotPipeline(intent, deps)
      // The delegated plan keeps its own label so telemetry reports the endpoints that ran.
      case 'complete':
      case 'master-publish':
      case 'restore-master-version':
      case 'restore-daily-version':
      case 'sync-daily-to-master':
        return v1BulkStrategy.plan(intent, deps)
      default:
        return assertNever(intent)
    }
  },
}
